// Admin: list of all merchants with KYB status.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Filter as Loader2, MapPin, Download } from 'lucide-react';
import { toast } from 'sonner';
import { PortalShell } from '../../components/shared/PortalShell';
import { Card, StatusPill } from '../../components/shared/primitives';
import { DataTable, AvatarCell, type Column } from '../../components/shared/DataTable';
import { MapView } from '../../components/shared/MapView';
import { Button } from '../../components/stripe-ui/Button';
import { ADMIN_NAV } from '../_navs';
import { admin, certificates, type AdminMerchantRow, type CertLang } from '../../lib/api';

type KybStatus = 'kyb_review' | 'approved' | 'rejected' | 'suspended';
type KybTone = 'success' | 'danger' | 'outline';

// Allowed next-steps per current status (mirrors the backend KYB_EDGES).
const KYB_ACTIONS: Record<string, Array<{ to: KybStatus; label: string; tone: KybTone }>> = {
  applied:    [{ to: 'approved', label: 'Approve', tone: 'success' }, { to: 'kyb_review', label: 'Review', tone: 'outline' }, { to: 'rejected', label: 'Reject', tone: 'danger' }],
  kyb_review: [{ to: 'approved', label: 'Approve', tone: 'success' }, { to: 'rejected', label: 'Reject', tone: 'danger' }],
  approved:   [{ to: 'suspended', label: 'Suspend', tone: 'danger' }],
  suspended:  [{ to: 'approved', label: 'Reinstate', tone: 'success' }],
  rejected:   [{ to: 'kyb_review', label: 'Re-open', tone: 'outline' }] };

const FILTERS = [
  { value: 'all',         label: 'All' },
  { value: 'applied',     label: 'Applied' },
  { value: 'kyb_review',  label: 'KYB review' },
  { value: 'approved',    label: 'Approved' },
  { value: 'suspended',   label: 'Suspended' },
  { value: 'rejected',    label: 'Rejected' },
];

export function AdminMerchantsPage() {
  const q = useQuery({ queryKey: ['admin', 'merchants'], queryFn: () => admin.merchants() });
  const [filter, setFilter] = useState<string>('all');

  const rows = useMemo(() => {
    const list = q.data ?? [];
    if (filter === 'all') return list;
    return list.filter((m) => m.status === filter);
  }, [q.data, filter]);

  const mapMarkers = useMemo(() =>
    rows.filter((m) => m.latitude && m.longitude).map((m) => ({
      lat: Number(m.latitude), lng: Number(m.longitude),
      label: `${m.business_name} · ${m.city?.name ?? ''} · ${m.status}`,
      color: m.status === 'approved' ? '#1455E8' : (m.status === 'suspended' || m.status === 'rejected') ? '#B3261E' : '#8A5A00' })), [rows]);

  // The design also asks for Category, Orders 30d and Fulfillment columns.
  // AdminMerchantRow carries none of the three, so they are left out rather
  // than filled in — an admin approving a merchant on invented throughput
  // figures is worse than one working from fewer, true ones.
  const columns: Column<AdminMerchantRow>[] = [
    {
      key: 'business', header: 'Merchant', type: 'node', width: 'minmax(0,1.5fr)',
      value: (m) => (
        <Link to={`/admin/merchants/${m.user_id}`} className="block min-w-0 hover:underline">
          <AvatarCell name={m.business_name} />
          {m.merchant_code && (
            <span className="ml-[38px] block font-mono text-[10.5px] text-[var(--color-muted)]">
              {m.merchant_code}
            </span>
          )}
        </Link>
      ) },
    {
      key: 'owner', header: 'Owner', type: 'muted', width: 'minmax(0,1.3fr)',
      value: (m) => m.user?.email ?? '—' },
    {
      key: 'city', header: 'City', type: 'text', width: '130px',
      value: (m) => m.city?.name ?? '—' },
    {
      key: 'applied', header: 'Applied', type: 'muted', width: '110px',
      value: (m) => (m.user?.created_at ? new Date(m.user.created_at).toLocaleDateString() : '—') },
    {
      key: 'kyb', header: 'KYB', type: 'node', width: '120px',
      value: (m) => <StatusPill status={m.status} /> },
    {
      key: 'actions', header: '', type: 'node', width: '200px',
      value: (m) => (
        <span className="flex flex-col items-end gap-1.5">
          <KybActions m={m} />
          <CertDownload m={m} />
        </span>
      ) },
  ];

  return (
    <PortalShell nav={ADMIN_NAV} activeId="merchants" title="Merchants" subtitle="Approve KYB applications. Suspend bad actors.">
      <div className="space-y-4">
        {mapMarkers.length > 0 && (
          <Card padding="p-0" className="overflow-hidden">
            <div className="px-4 py-2.5 border-b border-[var(--color-hairline)] flex items-center justify-between gap-2 flex-wrap">
              <p className="text-[12px] font-semibold text-[var(--color-body)] inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[var(--color-muted)]" /> Merchant coverage · {mapMarkers.length} located</p>
              <span className="text-[10.5px] text-[var(--color-muted)]">🔵 approved · 🟠 in review · 🔴 suspended/rejected</span>
            </div>
            <MapView height="340px" markers={mapMarkers} />
          </Card>
        )}

        <DataTable
          title="Merchants"
          subtitle="KYB approval gates every payout."
          filters={FILTERS.map((f) => ({ key: f.value, label: f.label }))}
          activeFilter={filter}
          onFilter={(k) => setFilter(k as typeof filter)}
          columns={columns}
          rows={rows}
          rowKey={(m) => m.user_id}
          loading={q.isLoading}
          empty="No merchants match this filter."
        />
      </div>
    </PortalShell>
  );
}

function CertDownload({ m }: { m: AdminMerchantRow }) {
  const [lang, setLang] = useState<CertLang>('en');
  const [busy, setBusy] = useState(false);
  if (m.status !== 'approved') return null;

  const dl = async () => {
    setBusy(true);
    try { await certificates.forMerchant(m.user_id, lang); }
    catch (e) { toast.error((e as Error).message || 'Download failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex items-center gap-1">
      <select
        value={lang}
        onChange={(e) => setLang(e.target.value as CertLang)}
        className="text-[10.5px] border border-[var(--color-border)] rounded px-1 py-0.5 bg-white"
        aria-label="Certificate language"
      >
        <option value="en">EN</option><option value="fr">FR</option><option value="ht">HT</option>
      </select>
      <button onClick={dl} disabled={busy} className="inline-flex items-center gap-1 text-[11px] font-semibold text-[var(--color-secondary)] hover:text-[var(--color-ink)]">
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} Certificate
      </button>
    </div>
  );
}

function KybActions({ m }: { m: AdminMerchantRow }) {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: (v: { status: KybStatus; note?: string | null }) => admin.setMerchantStatus(m.user_id, v.status, v.note),
    onSuccess: (_res, v) => {
      toast.success(v.status === 'approved' ? `${m.business_name} approved` : `Merchant ${v.status.replace('_', ' ')}`);
      qc.invalidateQueries({ queryKey: ['admin', 'merchants'] });
    },
    onError: (e: Error) => toast.error(e.message || 'Update failed') });

  const acts = KYB_ACTIONS[m.status] ?? [];
  if (acts.length === 0) return null;

  const run = (to: KybStatus) => {
    if (to === 'rejected') {
      const note = window.prompt('Reason for rejection (optional):');
      if (note === null) return;                 // cancelled
      mut.mutate({ status: to, note: note || null });
    } else if (to === 'suspended') {
      if (window.confirm(`Suspend ${m.business_name}? They'll disappear from the storefront immediately.`)) mut.mutate({ status: to });
    } else {
      mut.mutate({ status: to });
    }
  };

  return (
    <div className="flex items-center justify-end gap-1.5 flex-wrap">
      {acts.map((a) => (
        <Button
          key={a.to}
          size="sm"
          tone={a.tone}
          onClick={() => run(a.to)}
          loading={mut.isPending && mut.variables?.status === a.to}
          disabled={mut.isPending}
        >
          {a.label}
        </Button>
      ))}
    </div>
  );
}

